import React, { useEffect, useState } from "react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { classNames, formattedLastActivity } from "../../utils";
import CheckBox from "../../shared-components/CheckBox";

import {
  AssignmentFilterData,
  AssignmentPopoverActions,
} from "./AssignmentFilterPopover";
import { useUpdateActorTask } from "../../api/UsersApi";
import LoadingSpinner from "../../shared-components/LoadingSpinner";
import {
  AssignmentData,
  AssignmentDomain,
  AssignmentModel,
  HeaderAssignmentHeader,
} from "./models";
import {
  CheckAssignmentTableHeader,
  checkAssignmentTaskColumnGroupClasses,
} from "./CheckAssignmentTableHeader";
import { filterDataByKey } from "../../utils/worflows";

type Props = {
  headers: HeaderAssignmentHeader[];
  data: AssignmentData[];
  domain: AssignmentDomain;
  model: AssignmentModel;
};

function sortData(
  data: AssignmentData[],
  order: "asc" | "desc"
): AssignmentData[] {
  if (order === "asc") {
    return [...data].sort((a, b) => a.email.localeCompare(b.email));
  }

  return [...data].sort((a, b) => b.email.localeCompare(a.email));
}

const CheckAssignmentTable = ({ headers, data, domain, model }: Props) => {
  const [filteredKeys, setFilteredKeys] = useState<{
    [key: string]: AssignmentFilterData[];
  }>();
  const [currentSort, setCurrentSort] = useState<"asc" | "desc">("asc");
  const [headerKey, setHeaderKey] = useState("");
  const [shownData, setShownData] = useState<AssignmentData[]>([]);

  useEffect(() => {
    if (data.length === 0) return;
    let filteredData = filterDataByKey(data, filteredKeys, "email");
    filteredData = filterDataByKey(filteredData, filteredKeys, "lastActive");
    filteredData = filterDataByKey(filteredData, filteredKeys, "lastTask");

    if (headerKey) {
      filteredData = filterDataByKey(filteredData, filteredKeys, headerKey);
    }
    const sortedData = sortData(filteredData, currentSort);

    setShownData(sortedData);
  }, [currentSort, data, filteredKeys, headerKey]);

  function handleAction(
    action: AssignmentPopoverActions,
    selection: AssignmentFilterData[] | undefined,
    headerKey: string | undefined
  ): void {
    setHeaderKey(headerKey ?? "");
    switch (action) {
      case AssignmentPopoverActions.asc:
        setCurrentSort("asc");
        break;
      case AssignmentPopoverActions.desc:
        setCurrentSort("desc");
        break;
      case AssignmentPopoverActions.cancel:
        setFilteredKeys({ ...filteredKeys, [headerKey ?? ""]: [] });
        break;
      case AssignmentPopoverActions.ok:
        setFilteredKeys({
          ...filteredKeys,
          [headerKey ?? ""]: selection ?? [],
        });
        break;
    }
  }

  function onCheckChange(
    actor_uuid: string,
    task: { key: string },
    checked: boolean
  ) {
    const newData = shownData.map((row) => {
      if (row.actor_uuid === actor_uuid) {
        if (checked) {
          row.tasks.push(task);
        } else {
          row.tasks = row.tasks.filter((t) => t.key !== task.key);
        }
      }
      return row;
    });
    setShownData(newData);
  }

  return (
    <div>
      <CheckAssignmentTableHeader
        headers={headers}
        data={data}
        onAction={(action, selection, headerKey) =>
          handleAction(action, selection, headerKey)
        }
        filterKeys={filteredKeys}
        shownData={shownData}
        domain={domain}
      ></CheckAssignmentTableHeader>
      {shownData.map((row, i) => {
        return (
          <CheckAssignmentTableRow
            actor_uuid={row.actor_uuid}
            email={row.email}
            lastActive={row.lastActive ?? ""}
            lastTask={row.lastTask ?? ""}
            tasks={row.tasks}
            headers={headers}
            model={model}
            domain={domain}
            isOddStyle={i % 2 !== 0}
            onCheckChange={(task, checked) =>
              onCheckChange(row.actor_uuid, task, checked)
            }
          ></CheckAssignmentTableRow>
        );
      })}
    </div>
  );
};

type CheckAssignmentTableRowProps = {
  actor_uuid: string;
  email: string;
  lastActive: string;
  lastTask: string;
  tasks: { key: string }[];
  headers: { name: string; key: string }[];
  isOddStyle?: boolean;
  model: AssignmentModel;
  domain: AssignmentDomain;
  onCheckChange: (task: { key: string }, checked: boolean) => void;
};

function CheckAssignmentTableRowCell({
  children,
  isCentered = true,
  className,
  columnGroupClasses,
}: {
  children: React.ReactNode;
  isCentered?: boolean;
  className?: string;
  columnGroupClasses?: string;
}) {
  return (
    <div
      className={classNames(
        className ?? "flex flex-1 py-5 px-2 font-extralight text-sm truncate",
        isCentered
          ? "items-center justify-center"
          : "justify-start items-center",
        columnGroupClasses ?? ""
      )}
    >
      {children}
    </div>
  );
}

function CheckAssignmentTableRow(props: CheckAssignmentTableRowProps) {
  const [tasks, setTasks] = useState<{ key: string }[]>([]);

  useEffect(() => {
    setTasks(props.tasks);
  }, [props.tasks]);

  function isTaskAssigned(header: { name: string; key: string }): boolean {
    return tasks.some((task) => task.key === header.key);
  }

  function onCheckChange(
    evt: React.ChangeEvent<HTMLInputElement>,
    task: {
      key: string;
    }
  ) {
    if (evt.target.checked) {
      setTasks([...tasks, task]);
    } else {
      setTasks(tasks.filter((t) => t.key !== task.key));
    }
    props.onCheckChange(task, evt.target.checked);
  }

  return (
    <div
      className={classNames(
        "flex border-x border-b",
        props.isOddStyle ? "bg-gray-50" : "bg-white"
      )}
    >
      <CheckAssignmentTableRowCell isCentered={false}>
        {props.email}
      </CheckAssignmentTableRowCell>
      {props.domain === AssignmentDomain.workflow && (
        <div className="w-[30rem]">
          <CheckAssignmentTableRowCell className="flex p-0 text-xs items-center h-full">
            <CheckAssignmentTableRowCell className="flex flex-1 items-center justify-center">
              <div>
                {props.lastActive &&
                  formattedLastActivity(props.lastActive)
                    .split("\n")
                    .map((p) => <div>{p}</div>)}
              </div>
            </CheckAssignmentTableRowCell>
            <CheckAssignmentTableRowCell className="flex flex-1 items-center">
              {props.lastTask}
            </CheckAssignmentTableRowCell>
          </CheckAssignmentTableRowCell>
        </div>
      )}
      {props.headers.map((headerTask, taskIndex) => (
        <CheckAssignmentTableRowCell
          key={headerTask.key}
          isCentered={true}
          columnGroupClasses={checkAssignmentTaskColumnGroupClasses(
            taskIndex
          )}
        >
          <AssignmentCheck
            actor_uuid={props.actor_uuid}
            headerTask={headerTask}
            checked={isTaskAssigned(headerTask)}
            domain={props.domain}
            model={props.model}
            isTaskAssigned={isTaskAssigned}
            onCheckChange={onCheckChange}
          />
        </CheckAssignmentTableRowCell>
      ))}
    </div>
  );
}

function AssignmentCheck({
  actor_uuid,
  headerTask,
  checked,
  domain,
  model,
  isTaskAssigned,
  onCheckChange,
}: {
  actor_uuid: string;
  headerTask: { name: string; key: string };
  checked: boolean;
  domain: AssignmentDomain;
  model: AssignmentModel;
  isTaskAssigned: (headerTask: { name: string; key: string }) => boolean;
  onCheckChange: (
    evt: React.ChangeEvent<HTMLInputElement>,
    task: {
      key: string;
    }
  ) => void;
}) {
  const { mutate: updateActorTask, isLoading, isError } = useUpdateActorTask();
  function onChange(
    evt: React.ChangeEvent<HTMLInputElement>,
    task: {
      key: string;
    }
  ) {
    const update = {
      actor_uuid: actor_uuid,
      domain: domain,
      model: model,
      key: task.key,
      value: false,
    };
    if (evt.target.checked) {
      update.value = true;
    }
    updateActorTask([update]);
    onCheckChange(evt, task);
  }
  return (
    <>
      {!isLoading && (
        <CheckBox
          checked={isTaskAssigned(headerTask)}
          onChange={(evt) => onChange(evt, headerTask)}
        />
      )}
      {isLoading && <LoadingSpinner />}
      {isError && <ExclamationTriangleIcon className="text-red-500 w-4" />}
    </>
  );
}
export default CheckAssignmentTable;
